import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { GroupId, UserId } from 'balus-common';
import { trpcClient } from '@framework/trpcClient';
import { useForegroundRevision } from '@framework/hooks/useForegroundRevision';

type LoadState = { groupId: GroupId; usageRatios: Map<UserId, number | null> | null; failed: boolean };

/**
 * グループメンバー全員の当月のAI利用状況を取得する（グループ管理者のみ）。
 *
 * メンバー全員分の利用額はクライアントから購読できないので tRPC で割合だけを受け取る。
 * 更新の通知が来ないため、タブが前面に戻ったら取り直す。失敗しても直前の値は捨てない。
 */
export const useGroupMemberAIUsages = (
  groupId: GroupId,
  { skip = false }: { skip?: boolean } = {}
): { usageRatios: Map<UserId, number | null> | null; failed: boolean } => {
  const [state, setState] = useState<LoadState | null>(null);
  const requestSequenceRef = useRef(0);
  const foregroundRevision = useForegroundRevision({ skip });

  useEffect(() => {
    // 先に進行中の要求を無効化する。閉じた後に届いた応答が古い値を復活させないため
    const sequence = ++requestSequenceRef.current;

    if (skip) {
      setState(null);
      return;
    }

    trpcClient.group.listMemberAIUsage
      .query({ groupId })
      .then(({ members }) => {
        // 応答の到着順が入れ替わっても、最後に投げた要求の結果だけを採用する
        if (sequence !== requestSequenceRef.current) return;
        setState({
          groupId,
          usageRatios: new Map(members.map(({ userId, usedRatio }) => [userId, usedRatio])),
          failed: false,
        });
      })
      .catch((error) => {
        if (sequence !== requestSequenceRef.current) return;
        Sentry.captureException(error);
        setState((current) => ({
          groupId,
          usageRatios: current?.groupId === groupId ? current.usageRatios : null,
          failed: true,
        }));
      });
  }, [skip, groupId, foregroundRevision]);

  // 別のグループへ切り替えた直後に、前のグループの値を見せない
  const current = state?.groupId === groupId ? state : null;
  return { usageRatios: current?.usageRatios ?? null, failed: current?.failed ?? false };
};
